import React, { useEffect, useState } from 'react';
import { Modal, TextInput } from '@mantine/core';
import { Button } from '../../../../components/Button';
import { AdminSessionAuditView } from '../../../../services/admin-sessions/admin-sessions.service';
import { SessionFilterOption } from '../../../../services/past-receipts/past-receipts.service';
import { asCurrency } from '../../shared/utils/dashboard-page.utils';
import { FilterMode } from '../../shared/types/dashboard-page.types';
import classes from './filter-dialog.module.scss';

const asSessionDate = (iso: string): string => new Date(iso).toLocaleDateString('he-IL', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const toDateInputValue = (date: Date | null): string => {
  if (!date) {
    return '';
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const fromDateInputValue = (value: string): Date | null => {
  if (!value) {
    return null;
  }
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export type FilterDialogProps = {
  isFilterDialogOpen: boolean;
  setIsFilterDialogOpen: (open: boolean) => void;
  filterMode: FilterMode;
  setFilterMode: (mode: FilterMode) => void;
  tenantSessionOptions: SessionFilterOption[];
  sessions: AdminSessionAuditView[];
  pendingSessionId: string | null;
  setPendingSessionId: (id: string | null) => void;
  rangeStart: Date | null;
  rangeEnd: Date | null;
  setDateRange: (range: [Date | null, Date | null]) => void;
  setLoadedSessionIds: (ids: number[]) => void;
};

export const FilterDialog: React.FC<FilterDialogProps> = ({
  isFilterDialogOpen,
  setIsFilterDialogOpen,
  filterMode,
  setFilterMode,
  tenantSessionOptions,
  sessions,
  pendingSessionId,
  setPendingSessionId,
  rangeStart,
  rangeEnd,
  setDateRange,
  setLoadedSessionIds,
}) => {
  const [focusedField, setFocusedField] = useState<'start' | 'end' | null>(null);
  const [pendingFilterMode, setPendingFilterMode] = useState<FilterMode>(filterMode);
  const [pendingRangeStart, setPendingRangeStart] = useState<Date | null>(rangeStart);
  const [pendingRangeEnd, setPendingRangeEnd] = useState<Date | null>(rangeEnd);

  useEffect(() => {
    if (!isFilterDialogOpen) {
      return;
    }
    setPendingFilterMode(filterMode);
    setPendingRangeStart(rangeStart);
    setPendingRangeEnd(rangeEnd);
  }, [filterMode, isFilterDialogOpen, rangeEnd, rangeStart]);

  const sessionCards = tenantSessionOptions.length > 0
    ? tenantSessionOptions.map((option) => {
      const session = sessions.find((row) => row.sessionId === option.id);
      return {
        id: option.id,
        name: option.name,
        openTime: option.date,
        state: session?.state ?? option.state,
        salesTotal: session?.salesTotal ?? null,
      };
    })
    : sessions.map((row) => ({
      id: row.sessionId,
      name: row.sessionName || `#${row.sessionId}`,
      openTime: row.openTime,
      state: row.state,
      salesTotal: row.salesTotal,
    }));

  return (
    <Modal
      opened={isFilterDialogOpen}
      onClose={() => setIsFilterDialogOpen(false)}
      title="בחירת תאריך"
      classNames={{
        content: classes.modalContent,
        header: classes.modalHeader,
        title: classes.modalTitle,
        body: classes.modalBody,
        close: classes.modalClose,
      }}
      centered
      closeButtonProps={{ 'data-testid': 'dashboard-filter-close' } as Record<string, string>}
    >
      <div className={classes.tabRow}>
        <Button
          variant="cancel"
          className={pendingFilterMode === 'shift' ? `${classes.tab} ${classes.tabActive}` : classes.tab}
          onClick={() => setPendingFilterMode('shift')}
          ariaLabel="בחירת מצב משמרת"
          dataTestId="dashboard-filter-tab-shift"
        >
          משמרת
        </Button>
        <Button
          variant="cancel"
          className={pendingFilterMode === 'range' ? `${classes.tab} ${classes.tabActive}` : classes.tab}
          onClick={() => setPendingFilterMode('range')}
          ariaLabel="בחירת מצב טווח תאריכים"
          dataTestId="dashboard-filter-tab-range"
        >
          טווח תאריכים
        </Button>
      </div>

      {pendingFilterMode === 'shift' ? (
        <>
          <div className={classes.sessionList}>
            {sessionCards.map((card) => (
              <Button
                variant="cancel"
                key={card.id}
                className={String(card.id) === pendingSessionId ? `${classes.sessionCard} ${classes.sessionCardActive}` : classes.sessionCard}
                onClick={() => setPendingSessionId(String(card.id))}
                ariaLabel={`בחירת משמרת ${card.name}`}
                dataTestId={`dashboard-filter-session-${card.id}`}
              >
                <div className={classes.sessionCardMain}>
                  <div className={classes.sessionCardTop}>
                    <span className={classes.sessionCardDate}>{asSessionDate(card.openTime)}</span>
                  </div>
                  <div className={classes.sessionCardBottom}>
                    <span className={classes.sessionCardName}>{card.name}</span>
                    {card.state ? (
                      <span className={card.state === 'open' ? classes.sessionCardBadgeOpen : classes.sessionCardBadgeClosed}>
                        {card.state === 'open' ? 'פתוח' : 'סגור'}
                      </span>
                    ) : null}
                    {card.salesTotal !== null ? <span className={classes.sessionCardAmount}>{asCurrency(card.salesTotal)}</span> : null}
                  </div>
                </div>
              </Button>
            ))}
          </div>
          {sessionCards.length === 0 ? <p className={classes.emptyHint}>לא נמצאו משמרות בטבלת ההפעלות.</p> : null}
        </>
      ) : (
        <div className={classes.rangeFields}>
          <div className={classes.rangeField}>
            <label className={classes.rangeLabel} htmlFor="dashboard-range-start">מתאריך:</label>
            <TextInput
              id="dashboard-range-start"
              type="date"
              data-testid="dashboard-filter-range-start"
              classNames={{ input: focusedField === 'start' ? `${classes.dateInput} ${classes.dateInputFocused}` : classes.dateInput }}
              value={toDateInputValue(pendingRangeStart)}
              max={toDateInputValue(pendingRangeEnd) || undefined}
              onChange={(event) => setPendingRangeStart(fromDateInputValue(event.target.value))}
              onFocus={() => setFocusedField('start')}
              onBlur={() => setFocusedField((current) => (current === 'start' ? null : current))}
            />
          </div>
          <div className={classes.rangeField}>
            <label className={classes.rangeLabel} htmlFor="dashboard-range-end">עד תאריך:</label>
            <TextInput
              id="dashboard-range-end"
              type="date"
              data-testid="dashboard-filter-range-end"
              classNames={{ input: focusedField === 'end' ? `${classes.dateInput} ${classes.dateInputFocused}` : classes.dateInput }}
              value={toDateInputValue(pendingRangeEnd)}
              min={toDateInputValue(pendingRangeStart) || undefined}
              onChange={(event) => setPendingRangeEnd(fromDateInputValue(event.target.value))}
              onFocus={() => setFocusedField('end')}
              onBlur={() => setFocusedField((current) => (current === 'end' ? null : current))}
            />
          </div>
        </div>
      )}

      <Button
        variant="save"
        className={classes.confirmButton}
        dataTestId="dashboard-filter-confirm"
        disabled={pendingFilterMode === 'range' && (!pendingRangeStart || !pendingRangeEnd)}
        onClick={() => {
          if (pendingFilterMode === 'shift' && pendingSessionId) {
            setLoadedSessionIds([Number(pendingSessionId)]);
          } else if (pendingFilterMode === 'range') {
            setDateRange([pendingRangeStart, pendingRangeEnd]);
          }

          setFilterMode(pendingFilterMode);
          setIsFilterDialogOpen(false);
        }}
      >
        אישור
      </Button>
    </Modal>
  );
};
